import React, { useCallback, useState } from 'react'
import { moduleContext } from 'react-contextual'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'ramda'
import { partyApiSelectors } from '../api/party'
import { portfolioApiSelectors } from '../api/portfolio'
import { walletApiEvents, walletApiSelectors } from '../api/wallet'
import { apiStateSelectors } from '../api/apiState'
import { $TSFixMe } from 'types/ts-migrate'
import { bankingApiEvents, bankingApiSelectors } from 'modules/api/banking'
import { TBankingPartyErrors } from 'modules/api/banking/types'
import { TPartyRequest, TRefundRequest } from 'modules/api/wallet/types'

type RefundProps = {
  tenantId?: string
  tenantAccount?: $TSFixMe // use API type
  tenantName?: string
  tenantDetails?: $TSFixMe
  /** `null` while the balance is still loading — see `walletApiSelectors.getDepositAccountBalance`. */
  depositAccountBalance?: number | null
  asset?: $TSFixMe // use API type
}

interface RefundProviderProps {
  context: React.Context<$TSFixMe>
  children: React.ReactNode
  entity: 'portfolio' | 'party'
  id: string
  /** Required for contacts view. There are multiple accounts in the account selector */
  tenantAccountId?: string | false
  [key: string]: $TSFixMe
}

/**
 * Supports deposit refund state for both portfolios and parties
 */
const RefundProvider = moduleContext()(({ context, children, ...props }: RefundProviderProps) => {
  const { entity, id, tenantAccountId = false } = props
  const [isOpen, setIsOpen] = useState(false)
  // The payload last dispatched, so its apiState error bucket can be looked up (see WithdrawModal).
  const [requestPayload, setRequestPayload] = useState<TPartyRequest<TRefundRequest> | null>(null)
  const dispatch = useDispatch()

  const fetchPartyErrors = useCallback(
    (partyId: string) => {
      dispatch(bankingApiEvents.partyErrors_request({ partyId }))
    },
    [dispatch],
  )
  const getPartyAccountByPortfolioId = useSelector(partyApiSelectors.getPartyAccountByPortfolioId)
  const getPartyAccountByAccountId = useSelector(partyApiSelectors.getPartyAccountByAccountId)
  const getPartyNameById = useSelector(partyApiSelectors.getPartyNameById)
  const getPartyDetailsById = useSelector(partyApiSelectors.getPartyDetailsById)
  const getDepositAccountBalance = useSelector(walletApiSelectors.getDepositAccountBalance)
  const getBalanceAsset = useSelector(walletApiSelectors.getBalanceAsset)
  const getGeneralFormErrorsByEvent = useSelector(apiStateSelectors.getGeneralFormErrorsByEvent)
  const getFormFieldErrorsByEvent = useSelector(apiStateSelectors.getFormFieldErrorsByEvent)
  const refundEvent = requestPayload ? walletApiEvents.refund_request(requestPayload) : null
  const generalErrors = refundEvent ? getGeneralFormErrorsByEvent(refundEvent) : []
  const getPartyBankingErrors = useSelector(bankingApiSelectors.getBankingPartyById)
  const getPrimaryTenantByPortfolioId = useSelector(portfolioApiSelectors.getPrimaryTenantByPortfolioId)
  const fieldErrors = refundEvent ? getFormFieldErrorsByEvent(refundEvent) : {}
  const hasApiErrors = generalErrors.length > 0 || !isEmpty(fieldErrors)

  const handleRequestRefund = useCallback(
    ({ body, params }: TPartyRequest<TRefundRequest>) => {
      setRequestPayload({ body, params })
      dispatch(walletApiEvents.refund_request({ body, params }))
    },
    [dispatch, setRequestPayload],
  )

  let refundProps: RefundProps = {}

  switch (entity) {
    case 'portfolio': {
      const portfolioTenantId = getPrimaryTenantByPortfolioId(id)
      const portfolioTenantAccount = getPartyAccountByPortfolioId(portfolioTenantId, id, 'Tenant')

      refundProps = {
        tenantId: portfolioTenantId as string,
        tenantAccount: portfolioTenantAccount,
        tenantName: getPartyNameById(portfolioTenantId),
        tenantDetails: getPartyDetailsById(portfolioTenantId),
        depositAccountBalance: getDepositAccountBalance(portfolioTenantId, portfolioTenantAccount?.accountId),
        asset: getBalanceAsset(portfolioTenantId, portfolioTenantAccount?.accountId, 'Tenant'),
      }
      break
    }
    case 'party':
      if (tenantAccountId) {
        const tenantAccount = getPartyAccountByAccountId(id, tenantAccountId)
        refundProps = {
          tenantId: id,
          tenantAccount,
          tenantName: getPartyNameById(id),
          tenantDetails: getPartyDetailsById(id),
          depositAccountBalance: getDepositAccountBalance(id, tenantAccount?.accountId),
          asset: getBalanceAsset(id, tenantAccount?.accountId, 'Tenant'),
        }
      }
      break

    default:
      break
  }

  const partyBankingErrors: TBankingPartyErrors | null | undefined = refundProps.tenantId
    ? getPartyBankingErrors(refundProps.tenantId)
    : null

  const handleOpen = useCallback(() => {
    if (refundProps.tenantId) {
      fetchPartyErrors(refundProps.tenantId)
    }
    setIsOpen(true)
  }, [setIsOpen, fetchPartyErrors, refundProps])

  const store = {
    refund: {
      handleClose: () => setIsOpen(false),
      handleOpen,
      requestRefund: handleRequestRefund,
      isOpen,
      hasApiErrors,
      generalErrors,
      partyBankingErrors,
      fieldErrors,
      isSubmitting: useSelector(state => apiStateSelectors.isLoading(state)([walletApiEvents.refund_request])),
      isLoading: useSelector(state => apiStateSelectors.isLoading(state)([walletApiEvents.balance_request])),
      ...refundProps,
    },
    ...props,
  }

  return <context.Provider value={store}>{children}</context.Provider>
})

export default RefundProvider
